import AttachFileRounded from '@mui/icons-material/AttachFileRounded';
import HourglassBottomRounded from '@mui/icons-material/HourglassBottomRounded';
import SupportAgentRounded from '@mui/icons-material/SupportAgentRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailRounded from '@mui/icons-material/EmailRounded';
import DeliveryDiningRounded from '@mui/icons-material/DeliveryDiningRounded';
import TextSnippetRounded from '@mui/icons-material/TextSnippetRounded';
import { TEMPLATE_PLACEHOLDERS } from '../messages/templates';
import { RichTextEditor } from '../../components/RichTextEditor';
import { htmlText } from '../../lib/richText';
import { PackbytesMailPanel, PackbytesWhatsappPanel } from '../messages/PackbytesPanels';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError, unwrap, type Schemas } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { PoweredBy } from '../../components/PoweredBy';
import { ErrorState, LoadingRows, PageHeader } from '../../components/states';
import { IconBadge, Panel } from '../../components/ui';
import { dateTime } from '../../lib/format';
import { useNotify } from '../../lib/notify';

type PlatformSettings = Schemas['PlatformSettings'];
type SettingsPatch = Omit<Schemas['UpdatePlatformSettingsRequest'], 'version'>;

export const settingsKey = ['platform', 'settings'] as const;
export const TRIAL_PRESETS = [7, 14, 15, 30, 60, 90];
export const GRACE_OPTIONS = Array.from({ length: 15 }, (_, i) => i + 1);
export const TRIAL_USER_PRESETS = [1, 2, 3, 5];
export const TRIAL_MIN = 1;
export const TRIAL_MAX = 365;
const PHONE = /^\+?[0-9][0-9 -]{5,28}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const toInt = (v: string) => (/^\d+$/.test(v) ? Number(v) : NaN);

/**
 * `/settings`: platform-wide settings — trial, grace, trial user limit, the PackBytes contact, add-on
 * fees, and PackBytes' own WhatsApp number and mail server.
 */
export function SettingsPage() {
  const settings = useQuery({
    queryKey: settingsKey,
    queryFn: () => unwrap(api.GET('/platform/settings')),
  });
  return (
    <>
      <PageHeader
        title="Settings"
        subtitle="Platform-wide settings. Changes are recorded in the platform audit log."
      />
      {settings.isPending ? (
        <Panel>
          <LoadingRows rows={3} />
        </Panel>
      ) : settings.isError ? (
        <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
      ) : (
        <Stack spacing={2.5} key={settings.data.version}>
          <TrialPanel settings={settings.data} />
          <ContactPanel settings={settings.data} />
          <FeePanel settings={settings.data} plugin="whatsapp" />
          <FeePanel settings={settings.data} plugin="email" />
          <FeePanel settings={settings.data} plugin="foodpanda" />
          <PackbytesWhatsappPanel />
          <PackbytesMailPanel />
          <TemplatesPanel settings={settings.data} />
          <AttachmentsPanel settings={settings.data} />
        </Stack>
      )}
      <PoweredBy sx={{ mt: 4 }} />
    </>
  );
}

/** Saves a partial change with the row's version; a conflict reloads and shows a notice. */
function useSaveSettings(settings: PlatformSettings, onConflict: () => void) {
  const qc = useQueryClient();
  const notify = useNotify();
  return useMutation({
    mutationFn: (changes: SettingsPatch) =>
      unwrap(api.PATCH('/platform/settings', { body: { ...changes, version: settings.version } })),
    onSuccess: (saved) => qc.setQueryData(settingsKey, saved),
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'VERSION_CONFLICT') {
        onConflict();
        void qc.invalidateQueries({ queryKey: settingsKey });
      } else notify.error(e);
    },
  });
}

function ConflictNotice({ onClose }: { onClose: () => void }) {
  return (
    <Alert severity="warning" onClose={onClose}>
      Another admin changed these settings just now. The latest values are shown — check them and
      save again.
    </Alert>
  );
}

function SaveBar({
  settings,
  canEdit,
  dirty,
  valid,
  pending,
  onReset,
  onSave,
}: {
  settings: PlatformSettings;
  canEdit: boolean;
  dirty: boolean;
  valid: boolean;
  pending: boolean;
  onReset: () => void;
  onSave: () => void;
}) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
    >
      <Typography variant="caption" color="text.secondary">
        Last changed {dateTime(settings.updatedAt)}
      </Typography>
      {canEdit ? (
        <Stack direction="row" spacing={1}>
          <Button disabled={!dirty || pending} onClick={onReset}>
            Reset
          </Button>
          <Button
            variant="contained"
            disabled={!dirty || !valid}
            loading={pending}
            onClick={onSave}
          >
            Save changes
          </Button>
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Only super admins can change these settings.
        </Typography>
      )}
    </Stack>
  );
}

function TrialPanel({ settings }: { settings: PlatformSettings }) {
  const { can } = useAuth();
  const canEdit = can('settings.manage');
  const notify = useNotify();
  const [trial, setTrial] = useState(String(settings.trialDays));
  const [grace, setGrace] = useState(settings.graceDays);
  const [users, setUsers] = useState(String(settings.trialMaxUsers));
  const [conflict, setConflict] = useState(false);
  const save = useSaveSettings(settings, () => setConflict(true));

  const trialDays = toInt(trial);
  const trialValid = trialDays >= TRIAL_MIN && trialDays <= TRIAL_MAX;
  const trialUsers = toInt(users);
  const usersValid = trialUsers >= 1 && trialUsers <= 1000;
  const dirty =
    trialDays !== settings.trialDays ||
    grace !== settings.graceDays ||
    trialUsers !== settings.trialMaxUsers;

  const submit = () =>
    save.mutate(
      {
        ...(trialDays !== settings.trialDays ? { trialDays } : {}),
        ...(grace !== settings.graceDays ? { graceDays: grace } : {}),
        ...(trialUsers !== settings.trialMaxUsers ? { trialMaxUsers: trialUsers } : {}),
      },
      {
        onSuccess: (saved) =>
          notify.success(
            `New shops now get a ${String(saved.trialDays)}-day trial with up to ${String(saved.trialMaxUsers)} users`,
          ),
      },
    );

  return (
    <Panel
      title={
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          <IconBadge icon={<HourglassBottomRounded />} />
          <span>New shop trial</span>
        </Stack>
      }
      subtitle="How long a shop can use the app free after it signs up, how many users it may have meanwhile, and the grace period after its time ends."
      sx={{ maxWidth: 760 }}
    >
      <Stack spacing={2.5}>
        {conflict && <ConflictNotice onClose={() => setConflict(false)} />}
        <Box>
          <TextField
            label="Trial period"
            value={trial}
            onChange={(e) => setTrial(e.target.value.replace(/\D/g, '').slice(0, 3))}
            disabled={!canEdit}
            error={!trialValid}
            helperText={
              trialValid
                ? 'Shops that sign up from now on get this many free days.'
                : `Enter ${String(TRIAL_MIN)}–${String(TRIAL_MAX)} days`
            }
            slotProps={{
              htmlInput: { inputMode: 'numeric' },
              input: { endAdornment: <InputAdornment position="end">days</InputAdornment> },
            }}
            sx={{ maxWidth: 260 }}
          />
          {canEdit && (
            <Stack
              direction="row"
              sx={{ flexWrap: 'wrap', gap: 1, mt: 1.5 }}
              aria-label="Common trial lengths"
            >
              {TRIAL_PRESETS.map((d) => (
                <Chip
                  key={d}
                  label={`${String(d)} days`}
                  onClick={() => setTrial(String(d))}
                  color={trialDays === d ? 'primary' : 'default'}
                  variant={trialDays === d ? 'filled' : 'outlined'}
                />
              ))}
            </Stack>
          )}
        </Box>
        <Box>
          <TextField
            label="Users on trial"
            value={users}
            onChange={(e) => setUsers(e.target.value.replace(/\D/g, '').slice(0, 4))}
            disabled={!canEdit}
            error={!usersValid}
            helperText={
              usersValid
                ? 'Active users plus pending invitations a shop may have until its first payment. Raise it for one shop from its Subscription tab.'
                : 'Enter 1–1000 users'
            }
            slotProps={{
              htmlInput: { inputMode: 'numeric' },
              input: { endAdornment: <InputAdornment position="end">users</InputAdornment> },
            }}
            sx={{ maxWidth: 520 }}
          />
          {canEdit && (
            <Stack
              direction="row"
              sx={{ flexWrap: 'wrap', gap: 1, mt: 1.5 }}
              aria-label="Common trial user limits"
            >
              {TRIAL_USER_PRESETS.map((n) => (
                <Chip
                  key={n}
                  label={n === 1 ? '1 user' : `${String(n)} users`}
                  onClick={() => setUsers(String(n))}
                  color={trialUsers === n ? 'primary' : 'default'}
                  variant={trialUsers === n ? 'filled' : 'outlined'}
                />
              ))}
            </Stack>
          )}
        </Box>
        <TextField
          select
          label="Grace period"
          value={grace}
          onChange={(e) => setGrace(Number(e.target.value))}
          disabled={!canEdit}
          helperText="Days a shop keeps working after its trial or paid time ends, before it locks."
          sx={{ maxWidth: 260 }}
        >
          {GRACE_OPTIONS.map((d) => (
            <MenuItem key={d} value={d}>
              {d === 1 ? '1 day' : `${String(d)} days`}
            </MenuItem>
          ))}
        </TextField>
        <Alert severity="info" variant="outlined">
          Trial length and grace apply to shops that sign up from now on; shops that already signed
          up keep their dates (open one under Tenants → Subscription → Extend to give more time).
          The user limit applies to every shop still on its trial.
        </Alert>
        <SaveBar
          settings={settings}
          canEdit={canEdit}
          dirty={dirty}
          valid={trialValid && usersValid}
          pending={save.isPending}
          onReset={() => {
            setTrial(String(settings.trialDays));
            setGrace(settings.graceDays);
            setUsers(String(settings.trialMaxUsers));
          }}
          onSave={submit}
        />
      </Stack>
    </Panel>
  );
}

/** The PackBytes phone, WhatsApp and email shops see when renewing or when locked. */
function ContactPanel({ settings }: { settings: PlatformSettings }) {
  const { can } = useAuth();
  const canEdit = can('settings.manage');
  const notify = useNotify();
  const [phone, setPhone] = useState(settings.supportPhone ?? '');
  const [whatsapp, setWhatsapp] = useState(settings.supportWhatsapp ?? '');
  const [email, setEmail] = useState(settings.supportEmail ?? '');
  const [conflict, setConflict] = useState(false);
  const save = useSaveSettings(settings, () => setConflict(true));

  const norm = (v: string) => (v.trim() === '' ? null : v.trim());
  const phoneOk = !norm(phone) || PHONE.test(phone.trim());
  const whatsappOk = !norm(whatsapp) || PHONE.test(whatsapp.trim());
  const emailOk = !norm(email) || EMAIL.test(email.trim());
  const changes: SettingsPatch = {
    ...(norm(phone) !== settings.supportPhone ? { supportPhone: norm(phone) } : {}),
    ...(norm(whatsapp) !== settings.supportWhatsapp ? { supportWhatsapp: norm(whatsapp) } : {}),
    ...(norm(email) !== settings.supportEmail ? { supportEmail: norm(email) } : {}),
  };
  const dirty = Object.keys(changes).length > 0;

  return (
    <Panel
      title={
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          <IconBadge icon={<SupportAgentRounded />} />
          <span>PackBytes contact for shops</span>
        </Stack>
      }
      subtitle="Shown on a shop’s Subscription page and on the screen a shop sees when its subscription has ended."
      sx={{ maxWidth: 760 }}
    >
      <Stack spacing={2.25}>
        {conflict && <ConflictNotice onClose={() => setConflict(false)} />}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={!canEdit}
            error={!phoneOk}
            helperText={phoneOk ? ' ' : 'Use a number like +92 300 1234567'}
            placeholder="+92 300 1234567"
            slotProps={{ htmlInput: { inputMode: 'tel', maxLength: 30 } }}
            fullWidth
          />
          <TextField
            label="WhatsApp"
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
            disabled={!canEdit}
            error={!whatsappOk}
            helperText={whatsappOk ? ' ' : 'Use a number like +92 300 1234567'}
            placeholder="+92 300 1234567"
            slotProps={{ htmlInput: { inputMode: 'tel', maxLength: 30 } }}
            fullWidth
          />
        </Stack>
        <TextField
          label="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={!canEdit}
          error={!emailOk}
          helperText={emailOk ? 'Leave a field empty to hide it.' : 'Enter a valid email'}
          placeholder="support@packbytes.com"
          slotProps={{ htmlInput: { inputMode: 'email', maxLength: 254 } }}
          sx={{ maxWidth: { sm: 'calc(50% - 8px)' } }}
        />
        <SaveBar
          settings={settings}
          canEdit={canEdit}
          dirty={dirty}
          valid={phoneOk && whatsappOk && emailOk}
          pending={save.isPending}
          onReset={() => {
            setPhone(settings.supportPhone ?? '');
            setWhatsapp(settings.supportWhatsapp ?? '');
            setEmail(settings.supportEmail ?? '');
          }}
          onSave={() =>
            save.mutate(changes, { onSuccess: () => notify.success('Contact details saved') })
          }
        />
      </Stack>
    </Panel>
  );
}

/** The WhatsApp add-on's reference fee: suggested when enabling it, never shown to shops. */
const FEES = {
  whatsapp: { field: 'whatsappMonthlyFee', label: 'WhatsApp', icon: <WhatsAppIcon /> },
  email: { field: 'emailMonthlyFee', label: 'Email', icon: <EmailRounded /> },
  foodpanda: {
    field: 'foodpandaMonthlyFee',
    label: 'Foodpanda',
    icon: <DeliveryDiningRounded />,
  },
} as const;

/** The usual monthly fee of a paid add-on (reference only). */
function FeePanel({ settings, plugin }: { settings: PlatformSettings; plugin: keyof typeof FEES }) {
  const { can } = useAuth();
  const canEdit = can('settings.manage');
  const notify = useNotify();
  const { field, label, icon } = FEES[plugin];
  const saved = settings[field];
  const [fee, setFee] = useState(String(Number(saved)));
  const [conflict, setConflict] = useState(false);
  const save = useSaveSettings(settings, () => setConflict(true));
  const valid = /^\d{1,9}(\.\d{1,2})?$/.test(fee.trim());
  const dirty = valid && Number(fee) !== Number(saved);
  return (
    <Panel
      title={
        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
          <IconBadge icon={icon} />
          <span>{label} add-on</span>
        </Stack>
      }
      subtitle={`The usual monthly fee, suggested when you enable or extend ${label} for a shop. Each shop can be charged a different amount; shops never see this.`}
      sx={{ maxWidth: 760 }}
    >
      <Stack spacing={2.5}>
        {conflict && <ConflictNotice onClose={() => setConflict(false)} />}
        <TextField
          label="Monthly fee"
          value={fee}
          onChange={(e) => setFee(e.target.value.replace(/[^0-9.]/g, '').slice(0, 12))}
          disabled={!canEdit}
          error={!valid}
          helperText={
            valid ? 'Reference only. Revenue counts what was actually charged.' : 'Enter an amount'
          }
          slotProps={{
            htmlInput: { inputMode: 'decimal' },
            input: { startAdornment: <InputAdornment position="start">PKR</InputAdornment> },
          }}
          sx={{ maxWidth: 260 }}
        />
        <SaveBar
          settings={settings}
          canEdit={canEdit}
          dirty={dirty}
          valid={valid}
          pending={save.isPending}
          onReset={() => setFee(String(Number(saved)))}
          onSave={() =>
            save.mutate(
              { [field]: fee.trim() },
              { onSuccess: () => notify.success(`${label} fee saved`) },
            )
          }
        />
      </Stack>
    </Panel>
  );
}

/** Largest file a shop may attach to its records (1–10 MB; the server never takes more than 10). */
function AttachmentsPanel({ settings }: { settings: PlatformSettings }) {
  const { can } = useAuth();
  const canEdit = can('settings.manage');
  const notify = useNotify();
  const [mb, setMb] = useState(String(settings.attachmentMaxMb));
  const [conflict, setConflict] = useState(false);
  const save = useSaveSettings(settings, () => setConflict(true));
  const n = Number(mb);
  const valid = /^\d{1,2}$/.test(mb) && n >= 1 && n <= 10;
  const dirty = valid && n !== settings.attachmentMaxMb;
  return (
    <Panel
      title={
        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
          <IconBadge icon={<AttachFileRounded />} />
          <span>Attachments</span>
        </Stack>
      }
      subtitle="The largest file a shop can attach to its invoices, bills, customers and other records. Applies to every shop at once."
      sx={{ maxWidth: 760 }}
    >
      <Stack spacing={2.5}>
        {conflict && <ConflictNotice onClose={() => setConflict(false)} />}
        <TextField
          label="Largest file"
          value={mb}
          onChange={(e) => setMb(e.target.value.replace(/\D/g, '').slice(0, 2))}
          disabled={!canEdit}
          error={!valid}
          helperText={
            valid
              ? 'From 1 to 10 MB. Larger files are refused with a clear message.'
              : 'Enter 1 to 10'
          }
          slotProps={{
            htmlInput: { inputMode: 'numeric' },
            input: { endAdornment: <InputAdornment position="end">MB</InputAdornment> },
          }}
          sx={{ maxWidth: 200 }}
        />
        <SaveBar
          settings={settings}
          canEdit={canEdit}
          dirty={dirty}
          valid={valid}
          pending={save.isPending}
          onReset={() => setMb(String(settings.attachmentMaxMb))}
          onSave={() =>
            save.mutate(
              { attachmentMaxMb: n },
              { onSuccess: () => notify.success('Attachment size saved') },
            )
          }
        />
      </Stack>
    </Panel>
  );
}

const TEMPLATE_FIELDS = [
  'tenantWhatsappTemplate',
  'tenantEmailSubject',
  'tenantEmailBody',
  'inboxWhatsappTemplate',
  'inboxEmailSubject',
  'inboxEmailBody',
] as const;
type TemplateField = (typeof TEMPLATE_FIELDS)[number];
const HTML_FIELDS: TemplateField[] = ['tenantEmailBody', 'inboxEmailBody'];
const PLACEHOLDER_KEYS = TEMPLATE_PLACEHOLDERS.map((p) => `{${p.key}}`);

/**
 * What PackBytes' WhatsApp and Email buttons start with: one set for the tenant page (also used to
 * send activation codes, whose details are added below it) and one for support inbox conversations.
 * Email messages are rich text. The admin can still change the text before sending.
 */
function TemplatesPanel({ settings }: { settings: PlatformSettings }) {
  const { can } = useAuth();
  const canEdit = can('settings.manage');
  const notify = useNotify();
  const initial = () =>
    Object.fromEntries(TEMPLATE_FIELDS.map((f) => [f, settings[f]])) as Record<
      TemplateField,
      string
    >;
  const [values, setValues] = useState(initial);
  // Bumped on reset so the editors reload the saved text.
  const [generation, setGeneration] = useState(0);
  const [conflict, setConflict] = useState(false);
  const save = useSaveSettings(settings, () => setConflict(true));
  const filled = (f: TemplateField) =>
    (HTML_FIELDS.includes(f) ? htmlText(values[f]) : values[f].trim()).length > 0;
  const changed = TEMPLATE_FIELDS.filter((f) => values[f].trim() !== settings[f]);
  const valid = TEMPLATE_FIELDS.every(filled);
  const set = (f: TemplateField) => (v: string) => setValues((s) => ({ ...s, [f]: v }));
  const field = (f: TemplateField, label: string, multiline: boolean, max: number) => (
    <TextField
      label={label}
      value={values[f]}
      onChange={(e) => set(f)(e.target.value)}
      disabled={!canEdit}
      error={!filled(f)}
      multiline={multiline}
      minRows={multiline ? 3 : undefined}
      maxRows={multiline ? 10 : undefined}
      slotProps={{ htmlInput: { maxLength: max } }}
    />
  );
  const editor = (f: TemplateField) => (
    <RichTextEditor
      key={`${f}-${String(generation)}`}
      label="Email message"
      value={values[f]}
      onChange={set(f)}
      disabled={!canEdit}
      error={!filled(f)}
      placeholders={PLACEHOLDER_KEYS}
    />
  );
  const set_ = (title: string, hint: string, prefix: 'tenant' | 'inbox') => (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle2">{title}</Typography>
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      </Box>
      {field(`${prefix}WhatsappTemplate`, 'WhatsApp message', true, 4000)}
      {field(`${prefix}EmailSubject`, 'Email subject', false, 300)}
      {editor(`${prefix}EmailBody`)}
    </Stack>
  );
  return (
    <Panel
      title={
        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
          <IconBadge icon={<TextSnippetRounded />} />
          <span>Message templates</span>
        </Stack>
      }
      subtitle={`What PackBytes’ WhatsApp and Email buttons start with. Placeholders: ${TEMPLATE_PLACEHOLDERS.map((p) => `{${p.key}} ${p.label}`).join(' · ')}. You can still change the text before sending.`}
      sx={{ maxWidth: 860 }}
    >
      <Stack spacing={3}>
        {conflict && <ConflictNotice onClose={() => setConflict(false)} />}
        {set_(
          'Tenant page',
          'The buttons on a tenant’s page. Sending a new activation code uses it too; the code and its dates are added below.',
          'tenant',
        )}
        <Divider />
        {set_('Support inbox', 'The buttons on a support inbox conversation.', 'inbox')}
        <SaveBar
          settings={settings}
          canEdit={canEdit}
          dirty={changed.length > 0}
          valid={valid}
          pending={save.isPending}
          onReset={() => {
            setValues(initial());
            setGeneration((g) => g + 1);
          }}
          onSave={() =>
            save.mutate(Object.fromEntries(changed.map((f) => [f, values[f].trim()])), {
              onSuccess: () => notify.success('Templates saved'),
            })
          }
        />
      </Stack>
    </Panel>
  );
}
